import { Box, Button, IconButton, Typography } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CustomCellRendererProps } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useDeleteDomain } from '../hooks/api/use-domains.hook';
import { DomainApiModel } from '../../../types/models/domain.model';
import { useConfirm } from '../contexts/confirm.context';
import { InfniteScrollingGrid } from '../components/AgGrid/InfiniteScrollingGrid';
import { searchDomains } from '../api/domains.api';

export const Domains = () => {
  const navigate = useNavigate();
  const [showConfirm] = useConfirm();

  const [deleteDomain, { loading: deletingDomain, error: deleteError }] = useDeleteDomain();

  const columns: ColDef<DomainApiModel>[] = [
    { field: 'name', sortable: true, filter: true, filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } },
    {
      field: 'shortName',
      sortable: true,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'description',
      sortable: true,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'Actions',
      maxWidth: 200,
      cellRenderer: ({ api, node }: CustomCellRendererProps<DomainApiModel>) => (
        <>
          <IconButton onClick={() => navigate(`edit/${node.id}`)}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              showConfirm({
                onConfirm: async () => {
                  if (node.id) {
                    await deleteDomain({ domainId: +node.id });
                    api.refreshInfiniteCache();
                  }
                },
                title: 'Delete Domain',
                message: `Are you sure you want to delete domain "${node.data?.name}"?`,
                error: deleteError?.response?.data?.message,
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const getDomains: Parameters<typeof InfniteScrollingGrid<DomainApiModel>>[0]['onGetRows'] = async ({
    cursor,
    limit,
    filterQuery,
    sortQuery,
  }) => {
    const { data } = await searchDomains({
      ...filterQuery,
      cursor,
      limit,
      sort: sortQuery,
    });
    return data;
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title={'Domains'} />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }}>
          <Typography variant="h5">Manage Domains</Typography>
          <Box flexGrow={1} />
          <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`add`)}>
            Add Domain
          </Button>
        </Box>

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfniteScrollingGrid loading={deletingDomain} columnDefs={columns} onGetRows={getDomains} />
        </Box>
      </Box>
    </Box>
  );
};
